import React from 'react';
import LegalLayout from './LegalLayout';
import { useAuth } from '../../App';

// AGB_VERSION must stay in sync with backend AGB_VERSION so that acceptance
// tracking works. Bump this string whenever the terms materially change.
export const AGB_VERSION = '2026-02-12';

const Terms = () => {
  const { language } = useAuth();
  const isDe = language === 'de';

  return (
    <LegalLayout
      title={isDe ? 'Allgemeine Geschäftsbedingungen' : 'Terms & Conditions'}
      updated={isDe ? '12. Februar 2026' : 'February 12, 2026'}
      testId="terms-page"
    >
      {isDe ? (
        <>
          <p>
            Willkommen bei <strong>Padelarena Wädenswil</strong>. Die folgenden
            Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung unseres
            Online-Buchungssystems (<a href="https://bookpadelarena.ch">bookpadelarena.ch</a>)
            sowie unserer Padel-Courts, Golf-Simulatoren, Trainings und Events.
          </p>

          <h2>1. Geltungsbereich</h2>
          <p>
            Diese AGB gelten für alle Buchungen, Kurse, Abonnements, Mixins,
            Turniere und Konsumationen (Restaurant/Bar) bei der Padelarena Wädenswil.
            Mit der Registrierung eines Kontos bzw. der Nutzung unseres Angebots
            akzeptierst du die aktuelle Fassung dieser AGB.
          </p>

          <h2>2. Reservationen &amp; Zahlungen</h2>
          <ul>
            <li>Padel-Courts können in 30-Minuten-Blöcken gebucht werden, Golf-Simulatoren in 60-Minuten-Blöcken.</li>
            <li>Preise richten sich nach dem im Buchungsprozess angezeigten Tarif (Off-Peak / Peak / Wochenende).</li>
            <li>Zahlungen erfolgen online (TWINT, Karte, Apple Pay, Google Pay) oder vor Ort im Club (Bar/Karte).</li>
            <li>Mitglieder und Abo-Inhaber:innen erhalten die im jeweiligen Angebot vereinbarten Vergünstigungen.</li>
            <li>Bei Nicht-Bezahlung einer Reservation behalten wir uns das Recht vor, den Platz weiterzuvergeben.</li>
          </ul>

          <h2>3. Stornierungsbedingungen — Court-Buchungen</h2>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 my-4">
            <p className="!my-0 font-semibold text-emerald-900">
              ✓ Kostenlose Stornierung bis <strong>24 Stunden</strong> vor Spielbeginn.
            </p>
            <p className="!mt-2 !mb-0 text-sm text-slate-700">
              Storniere direkt in deinem Kundenkonto unter &bdquo;Meine Buchungen&ldquo;.
              Bezahlte Beträge werden auf dieselbe Zahlungsart zurückerstattet
              (5–10 Werktage).
            </p>
          </div>
          <p>
            Stornierungen <strong>weniger als 24 Stunden vor Spielbeginn</strong> sind
            nicht mehr möglich. Bereits gezahlte Beträge werden in diesem Fall
            <strong> nicht</strong> zurückerstattet. Nicht angetretene Slots
            (No-Show) gelten ebenfalls als konsumiert.
          </p>
          <p>
            Ausnahmen (z.B. Krankheit mit Arztzeugnis, technisches Problem im
            Club) prüfen wir im Einzelfall — bitte kontaktiere{' '}
            <a href="mailto:info@padelarena.ch">info@padelarena.ch</a>.
          </p>

          <h2>4. Stornierungsbedingungen — Mixins, Turniere &amp; Events</h2>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 my-4">
            <p className="!my-0 font-semibold text-amber-900">
              ⚠ Kostenlose Abmeldung bis <strong>2 Stunden</strong> vor Eventbeginn.
            </p>
            <p className="!mt-2 !mb-0 text-sm text-slate-700">
              Abmeldung innerhalb der 2-Stunden-Frist: <strong>keine Rückerstattung</strong>.
              Der bezahlte Betrag verbleibt beim Club. Der freigewordene Platz
              wird automatisch der Warteliste oder dem nächsten Anmeldenden
              als Gratis-Platz angeboten.
            </p>
          </div>
          <p>
            Wir behalten uns vor, Events bei zu geringer Teilnehmerzahl oder aus
            organisatorischen Gründen abzusagen. In diesem Fall werden bereits
            geleistete Zahlungen vollumfänglich zurückerstattet.
          </p>

          <h2>5. Abonnements (Abos)</h2>
          <ul>
            <li>Abos gelten personalisiert und sind nicht auf Dritte übertragbar.</li>
            <li>Bereits genutzte Lektionen können nicht rückerstattet werden.</li>
            <li>Bei Löschung/Stornierung einer per Abo bezahlten Buchung wird die Lektion automatisch zurück ins Abo gebucht.</li>
            <li>Abos verfallen 12 Monate nach Kauf, sofern nicht anders vereinbart.</li>
          </ul>

          <h2>6. Junior-Akademie</h2>
          <ul>
            <li>Anmeldungen für die Junior-Akademie erfolgen saisonal.</li>
            <li>Bei Krankheit ist eine Rückmeldung an <a href="mailto:info@padelarena.ch">info@padelarena.ch</a> erforderlich; Nachhol-Lektionen nach Absprache mit dem Trainer.</li>
            <li>Die Erziehungsberechtigten sind für den Weg zum und vom Club verantwortlich.</li>
          </ul>

          <h2>7. Verhalten im Club</h2>
          <ul>
            <li>Padel-Schuhe (mit Non-Marking-Sohlen) sind auf den Plätzen Pflicht.</li>
            <li>Glasflaschen und Speisen sind auf den Courts nicht erlaubt.</li>
            <li>Kinder unter 14 Jahren sind auf den Courts nur in Begleitung eines Erwachsenen oder Trainers erlaubt.</li>
            <li>Bei mutwilliger Beschädigung von Einrichtung oder Ausrüstung haftet der/die Verursacher:in.</li>
          </ul>

          <h2>8. Haftung</h2>
          <p>
            Die Nutzung der Anlage erfolgt auf eigene Gefahr. Padelarena
            Wädenswil haftet nicht für Personen- oder Sachschäden, die durch
            unsachgemässe Nutzung, sportliche Betätigung oder das Verhalten
            Dritter entstehen, ausser bei grober Fahrlässigkeit oder Vorsatz
            unsererseits. Für abhandengekommene Wertsachen wird keine Haftung
            übernommen.
          </p>

          <h2>9. Datenschutz</h2>
          <p>
            Die Verarbeitung deiner personenbezogenen Daten richtet sich nach
            unserer{' '}
            <a href="/privacy">Datenschutzerklärung</a>. Diese ist Bestandteil
            der AGB.
          </p>

          <h2>10. Migration von Playtomic</h2>
          <p>
            Kundendaten (Name, E-Mail, Telefon, Player-Level) aus dem bisherigen
            Playtomic-System wurden zur Weiterführung der Dienstleistung in
            unser eigenes System übertragen. Du hast jederzeit das Recht auf
            Löschung dieser Daten via{' '}
            <a href="mailto:info@padelarena.ch">info@padelarena.ch</a>.
          </p>

          <h2>11. Änderungen der AGB</h2>
          <p>
            Wir behalten uns Änderungen dieser AGB vor. Bei wesentlichen
            Änderungen wirst du beim nächsten Login um eine erneute Bestätigung
            gebeten.
          </p>

          <h2>12. Anwendbares Recht &amp; Gerichtsstand</h2>
          <p>
            Es gilt ausschliesslich Schweizer Recht. Gerichtsstand ist Wädenswil (ZH).
          </p>

          <h2>Kontakt</h2>
          <p>
            <strong>Padelarena Wädenswil</strong><br />
            E-Mail: <a href="mailto:info@padelarena.ch">info@padelarena.ch</a><br />
            Web: <a href="https://bookpadelarena.ch">bookpadelarena.ch</a>
          </p>

          <p className="!text-xs !text-slate-500 !mt-8">
            Version {AGB_VERSION}
          </p>
        </>
      ) : (
        // ============ ENGLISH ============
        <>
          <p>
            Welcome to <strong>Padelarena Wädenswil</strong>. These Terms &amp;
            Conditions govern the use of our online booking system
            (<a href="https://bookpadelarena.ch">bookpadelarena.ch</a>) and of our
            padel courts, golf simulators, trainings and events.
          </p>

          <h2>1. Scope</h2>
          <p>
            These Terms apply to all bookings, courses, subscriptions, mixins,
            tournaments and consumption (restaurant/bar) at Padelarena Wädenswil.
            By registering an account or using our services you accept the current
            version of these Terms.
          </p>

          <h2>2. Reservations &amp; Payments</h2>
          <ul>
            <li>Padel courts are bookable in 30-minute blocks; golf simulators in 60-minute blocks.</li>
            <li>Prices follow the tariff shown during checkout (off-peak / peak / weekend).</li>
            <li>Payments are accepted online (TWINT, card, Apple Pay, Google Pay) or on site (cash / card).</li>
            <li>Members and subscription holders receive the discounts of their respective plan.</li>
            <li>Unpaid reservations may be released to other players.</li>
          </ul>

          <h2>3. Cancellation policy — Court bookings</h2>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 my-4">
            <p className="!my-0 font-semibold text-emerald-900">
              ✓ Free cancellation up to <strong>24 hours</strong> before start.
            </p>
            <p className="!mt-2 !mb-0 text-sm text-slate-700">
              Cancel from your customer account under &ldquo;My Bookings&rdquo;.
              Paid amounts are refunded to the original payment method (5–10 business days).
            </p>
          </div>
          <p>
            Cancellations <strong>less than 24 hours before start</strong> are no
            longer possible; already paid amounts are <strong>not</strong> refunded.
            No-shows count as consumed.
          </p>
          <p>
            Exceptions (e.g. illness with medical certificate, club-side
            technical issue) are handled case-by-case — please contact{' '}
            <a href="mailto:info@padelarena.ch">info@padelarena.ch</a>.
          </p>

          <h2>4. Cancellation policy — Mixins, tournaments &amp; events</h2>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 my-4">
            <p className="!my-0 font-semibold text-amber-900">
              ⚠ Free cancellation up to <strong>2 hours</strong> before start.
            </p>
            <p className="!mt-2 !mb-0 text-sm text-slate-700">
              Cancellation within the 2-hour window: <strong>no refund</strong>.
              The paid amount stays with the club. The freed spot is
              automatically offered to the waitlist or the next signup as a
              free spot.
            </p>
          </div>
          <p>
            We reserve the right to cancel events for lack of participants or
            organisational reasons; in that case all paid amounts are fully refunded.
          </p>

          <h2>5. Subscriptions (Abos)</h2>
          <ul>
            <li>Subscriptions are personal and non-transferable.</li>
            <li>Consumed lessons cannot be refunded.</li>
            <li>If a booking paid via subscription is deleted/cancelled, the lesson is automatically credited back.</li>
            <li>Subscriptions expire 12 months after purchase unless agreed otherwise.</li>
          </ul>

          <h2>6. Junior Academy</h2>
          <ul>
            <li>Junior Academy signups happen seasonally.</li>
            <li>In case of illness please notify <a href="mailto:info@padelarena.ch">info@padelarena.ch</a>; make-up lessons are agreed with the coach.</li>
            <li>Parents/guardians are responsible for the way to and from the club.</li>
          </ul>

          <h2>7. Club rules</h2>
          <ul>
            <li>Padel shoes with non-marking soles are mandatory on the courts.</li>
            <li>Glass bottles and food are not allowed on the courts.</li>
            <li>Children under 14 are allowed on the courts only accompanied by an adult or coach.</li>
            <li>Wilful damage to equipment or facilities is liable to the person causing it.</li>
          </ul>

          <h2>8. Liability</h2>
          <p>
            Use of the facilities is at your own risk. Padelarena Wädenswil is
            not liable for personal or property damage caused by improper use,
            sports activity or third-party behaviour, except in cases of gross
            negligence or intent on our part. We take no liability for lost valuables.
          </p>

          <h2>9. Data protection</h2>
          <p>
            Processing of your personal data is governed by our{' '}
            <a href="/privacy">privacy policy</a>, which forms an integral part of these Terms.
          </p>

          <h2>10. Playtomic migration</h2>
          <p>
            Customer data (name, email, phone, player level) from the previous
            Playtomic system has been transferred into our own system to
            continue providing service to you. You may request deletion of this
            data at any time via{' '}
            <a href="mailto:info@padelarena.ch">info@padelarena.ch</a>.
          </p>

          <h2>11. Changes to these Terms</h2>
          <p>
            We reserve the right to change these Terms. In case of material
            changes, you&apos;ll be asked to re-accept them on your next login.
          </p>

          <h2>12. Applicable law &amp; jurisdiction</h2>
          <p>Swiss law applies exclusively. Jurisdiction is Wädenswil (ZH).</p>

          <h2>Contact</h2>
          <p>
            <strong>Padelarena Wädenswil</strong><br />
            Email: <a href="mailto:info@padelarena.ch">info@padelarena.ch</a><br />
            Web: <a href="https://bookpadelarena.ch">bookpadelarena.ch</a>
          </p>

          <p className="!text-xs !text-slate-500 !mt-8">
            Version {AGB_VERSION}
          </p>
        </>
      )}
    </LegalLayout>
  );
};

export default Terms;
